import { Funnel, Package, SlidersHorizontal } from 'lucide-react';

import {
    type AppliedFilters,
    type ChoiceFilterField,
    type FilterField,
    type FilterGroup,
    type FilterOption,
    filterText,
    rangeEnds,
} from '@/components/FilterBar/filters';
import type { NamedRow, OrderListQuery, OrderLookups } from '@/features/orders/api';
import {
    COMMISSION_STATUS,
    COMMISSION_STATUSES,
    lineStatusText,
    ORDER_STATUS,
    PAYMENT_STATUS,
} from '@/features/orders/orders';

/*
  The orders drawer's three groups, as the frames draw them (2495-17274 Order, 2495-17860 Product,
  2495-18464 Logistics): an icon tile, a title, an italic subtitle, and a field grid that opens one
  group at a time. Everything mirrors to the URL.

  The set is the legacy /OP/OR/ grid's, regrouped rather than reinvented: OrderSummaryFetch takes
  30 filter parameters and all 30 are here, plus two of ours. Commission Model is the supplier's, in
  als.SupplierProfile (ADR 0032), reached through the line's supplier; Commission Status is the
  order's own, in als.OrderCommission (ALS-288). The prototype greys both until Supplier Commission
  is Yes, and that greying is kept.

  Like the product register, the filtering happens on the server, so what this module owns is the
  drawer's shape and the translation of the applied set into the API's query. No predicate runs in
  the browser.
*/

export const ORDER_FILTER_KEYS = {
    // Order
    orderNo: 'orderNo',
    invoiceNo: 'invoiceNo',
    customer: 'customer',
    status: 'status',
    payment: 'payment',
    rm: 'rm',
    reference: 'reference',
    invoiceType: 'invoiceType',
    saleSource: 'saleSource',
    assignedTo: 'assignedTo',
    dateField: 'dateField',
    dates: 'dates',
    // Product
    product: 'product',
    itemStatus: 'itemStatus',
    brand: 'brand',
    productTax: 'productTax',
    supplier: 'supplier',
    commission: 'commission',
    commissionModel: 'commissionModel',
    commissionStatus: 'commissionStatus',
    sizeType: 'sizeType',
    size: 'size',
    categoryGroup: 'categoryGroup',
    cat1: 'cat1',
    cat2: 'cat2',
    cat3: 'cat3',
    // Logistics & display
    tracking: 'tracking',
    preOrder: 'preOrder',
    dropShip: 'dropShip',
    active: 'active',
    grid: 'grid',
} as const;

export type OrderFilterKey = (typeof ORDER_FILTER_KEYS)[keyof typeof ORDER_FILTER_KEYS];

/** What every choice filter uses for "not applied". The prototype writes it three ways; we write it once. */
export const ANY = 'All';

/** The two values the Grid / Report Type control takes. Summary drops the thumbnail column. */
export const GRID_DETAIL = 'Detail';
export const GRID_SUMMARY = 'Summary';

const YES = 'Yes';
const NO = 'No';

const yesNo = (allLabel = 'All'): FilterOption[] => [
    { value: ANY, label: allLabel },
    { value: YES, label: YES },
    { value: NO, label: NO },
];

const named = (allLabel: string, rows: readonly NamedRow[]): FilterOption[] => [
    { value: ANY, label: allLabel },
    ...rows.map((row) => ({ value: String(row.id), label: row.name })),
];

/** A choice whose only option is the neutral one is a dead control, so the drawer does not draw it. */
function withOptions(
    fields: readonly FilterField<OrderFilterKey>[],
): FilterField<OrderFilterKey>[] {
    return fields.filter(
        (field) =>
            field.kind !== 'choice' ||
            typeof field.options === 'function' ||
            field.options.length > 1,
    );
}

export interface OrderFilterLookups extends OrderLookups {
    /** Fetched only once the drawer opens, so the register's first paint does not carry 13,297 rows. */
    products: readonly NamedRow[];
}

export function orderFilterGroups(lookups: OrderFilterLookups): FilterGroup<OrderFilterKey>[] {
    const K = ORDER_FILTER_KEYS;

    const orderGroup: FilterGroup<OrderFilterKey> = {
        key: 'order',
        icon: Funnel,
        title: 'Order filters',
        subtitle: 'Narrow by order, customer & payment',
        fields: withOptions([
            {
                key: K.orderNo,
                kind: 'text',
                label: 'Order No',
                chipLabel: 'Order',
                placeholder: 'ALS/2026/…',
            },
            {
                key: K.invoiceNo,
                kind: 'text',
                label: 'Invoice No',
                chipLabel: 'Invoice',
                placeholder: 'INV/ALS/…',
            },
            {
                key: K.customer,
                kind: 'choice',
                label: 'Customer',
                chipLabel: 'Customer',
                neutral: ANY,
                // 531 clients: a menu does not scale to that, so the drawer draws a Combobox.
                searchable: true,
                options: named('Any', lookups.customers),
            },
            {
                key: K.status,
                kind: 'choice',
                label: 'Order Status',
                chipLabel: 'Status',
                neutral: ANY,
                // The four the design draws. Deleted is here on purpose: the legacy list hides
                // soft-deleted orders until somebody asks for them by name, and this is the ask.
                options: [
                    { value: ANY, label: 'Any' },
                    { value: 'InProgress', label: ORDER_STATUS.InProgress.label },
                    { value: 'Completed', label: ORDER_STATUS.Completed.label },
                    { value: 'Cancelled', label: ORDER_STATUS.Cancelled.label },
                    { value: 'Deleted', label: ORDER_STATUS.Deleted.label },
                ],
            },
            {
                key: K.payment,
                kind: 'choice',
                label: 'Payment status',
                chipLabel: 'Payment',
                neutral: ANY,
                options: [
                    { value: ANY, label: 'Any' },
                    { value: 'NotPaid', label: PAYMENT_STATUS.NotPaid.label },
                    { value: 'DepositUnpaid', label: PAYMENT_STATUS.DepositUnpaid.label },
                    { value: 'DepositPaid', label: PAYMENT_STATUS.DepositPaid.label },
                    { value: 'PartiallyPaid', label: PAYMENT_STATUS.PartiallyPaid.label },
                    { value: 'FullyPaid', label: PAYMENT_STATUS.FullyPaid.label },
                    { value: 'Refunded', label: PAYMENT_STATUS.Refunded.label },
                ],
            },
            {
                key: K.rm,
                kind: 'choice',
                label: 'Relationship Manager',
                chipLabel: 'RM',
                neutral: ANY,
                options: named('Any', lookups.relationshipManagers),
            },
            {
                key: K.reference,
                kind: 'text',
                label: 'Reference',
                chipLabel: 'Ref',
                placeholder: 'Input',
            },
            {
                key: K.invoiceType,
                kind: 'choice',
                label: 'Invoice Type',
                chipLabel: 'Invoice type',
                neutral: ANY,
                options: named('Any', lookups.invoiceTypes),
            },
            {
                key: K.saleSource,
                kind: 'choice',
                label: 'Sales Source',
                chipLabel: 'Source',
                neutral: ANY,
                searchable: true,
                options: named('Any', lookups.saleSources),
            },
            {
                key: K.assignedTo,
                kind: 'choice',
                label: 'Assigned To',
                chipLabel: 'Assigned',
                neutral: ANY,
                options: named('Any', lookups.assignedTo),
            },
            {
                key: K.dateField,
                kind: 'choice',
                label: 'Date Filter',
                chipLabel: 'Dates on',
                // Which date the range measures. Neutral is the order date, so picking it alone
                // narrows nothing and draws no chip; the range beside it is what filters.
                neutral: 'order',
                options: [
                    { value: 'order', label: 'Order date' },
                    { value: 'invoice', label: 'Invoice date' },
                    { value: 'estimatedDelivery', label: 'Est. delivery' },
                ],
            },
            // The frame's "From / To Date" is one cell holding both ends, opening a two-month
            // calendar, and explicitly not two boxes (2497:22154).
            { key: K.dates, kind: 'daterange', label: 'From / To Date', chipLabel: 'Dates' },
        ]),
    };

    const productGroup: FilterGroup<OrderFilterKey> = {
        key: 'product',
        icon: Package,
        title: 'Product filters',
        subtitle: 'Brand, category, supplier, size, colour',
        fields: withOptions([
            {
                key: K.product,
                kind: 'choice',
                label: 'Product',
                chipLabel: 'Product',
                neutral: ANY,
                searchable: true,
                options: named('All', lookups.products),
            },
            {
                key: K.itemStatus,
                kind: 'choice',
                label: 'Product Status',
                chipLabel: 'Product status',
                neutral: ANY,
                // The design's words for each status, as the rest of the admin prints them.
                options: named(
                    'All',
                    lookups.lineStatuses.map((row) => ({
                        id: row.id,
                        name: lineStatusText(row.status, row.name),
                    })),
                ),
            },
            {
                key: K.brand,
                kind: 'choice',
                label: 'Brand',
                chipLabel: 'Brand',
                neutral: ANY,
                searchable: true,
                options: named('All', lookups.brands),
            },
            {
                key: K.productTax,
                kind: 'choice',
                label: 'Product Tax',
                chipLabel: 'Tax',
                neutral: ANY,
                options: named('All', lookups.taxes),
            },
            {
                key: K.supplier,
                kind: 'choice',
                label: 'Supplier',
                chipLabel: 'Supplier',
                neutral: ANY,
                searchable: true,
                options: named('All', lookups.suppliers),
            },
            {
                key: K.commission,
                kind: 'choice',
                label: 'Supplier Commission',
                chipLabel: 'Commission',
                neutral: ANY,
                // The flag the order line carries. Commission Model and Commission Status sit
                // under it, as the frame draws them.
                options: yesNo('Select'),
            },
            {
                key: K.commissionModel,
                kind: 'choice',
                label: 'Commission Model',
                chipLabel: 'Commission model',
                neutral: ANY,
                /*
                  The supplier's model, not the line's: als.SupplierProfile carries one row per
                  supplier (ADR 0032), and the filter reaches it through the line's supplier. The
                  two the supplier register earns on (ALS-288). None is not offered: the field
                  only opens once Supplier Commission is Yes, and a supplier on None earns none.
                */
                options: [
                    { value: ANY, label: 'Select' },
                    { value: 'Auto', label: 'Auto' },
                    { value: 'OnRequest', label: 'On request' },
                ],
                // Greyed until Supplier Commission says Yes, as the frame draws it. The API has no
                // such rule: it is asked for a model or it is not.
                disabledWhen: (applied) => applied[K.commission] !== YES,
            },
            {
                key: K.commissionStatus,
                kind: 'choice',
                label: 'Commission Status',
                chipLabel: 'Commission status',
                neutral: ANY,
                /*
                  The order's own, not the line's: one status per order, Not paid until somebody
                  marks it Paid in Edit order (ALS-288). Only an order with a product that earns
                  commission has one, so either value leaves out every order without.
                */
                options: [
                    { value: ANY, label: 'Select' },
                    ...COMMISSION_STATUSES.map((status) => ({
                        value: status,
                        label: COMMISSION_STATUS[status].label,
                    })),
                ],
                disabledWhen: (applied) => applied[K.commission] !== YES,
            },
            {
                key: K.sizeType,
                kind: 'choice',
                label: 'Size Type',
                chipLabel: 'Size type',
                neutral: ANY,
                options: named('All', lookups.sizeTypes),
            },
            {
                key: K.size,
                kind: 'choice',
                label: 'Size',
                chipLabel: 'Size',
                neutral: ANY,
                searchable: true,
                options: named('All', lookups.sizes),
            },
            {
                key: K.categoryGroup,
                kind: 'choice',
                label: 'Category Group',
                chipLabel: 'Group',
                neutral: ANY,
                options: named('All', lookups.categoryGroups),
            },
            {
                key: K.cat1,
                kind: 'choice',
                label: 'Primary Category',
                chipLabel: 'Category',
                neutral: ANY,
                options: named('All', lookups.categories),
            },
            {
                key: K.cat2,
                kind: 'choice',
                label: 'Secondary Category',
                chipLabel: 'Sub-category',
                neutral: ANY,
                searchable: true,
                options: named('All', lookups.secondaryCategories),
            },
            {
                key: K.cat3,
                kind: 'choice',
                label: 'Tertiary Category',
                chipLabel: 'Tertiary',
                neutral: ANY,
                // Empty in the data today, so `withOptions` drops the field rather than drawing a
                // select whose only choice is All.
                options: named('All', lookups.tertiaryCategories),
            },
        ]),
    };

    const logisticsGroup: FilterGroup<OrderFilterKey> = {
        key: 'logistics',
        icon: SlidersHorizontal,
        title: 'Logistics & display',
        subtitle: 'Shipping, sourcing, sort, columns',
        fields: withOptions([
            {
                key: K.tracking,
                kind: 'text',
                label: 'Tracking Number',
                chipLabel: 'Tracking',
                placeholder: 'DHL…',
            },
            {
                key: K.preOrder,
                kind: 'choice',
                label: 'Product pre-order',
                chipLabel: 'Pre-order',
                neutral: ANY,
                options: yesNo(),
            },
            {
                key: K.dropShip,
                kind: 'choice',
                label: 'Drop Ship',
                chipLabel: 'Drop ship',
                neutral: ANY,
                options: yesNo(),
            },
            {
                key: K.active,
                kind: 'choice',
                label: 'Active',
                chipLabel: 'Active',
                neutral: ANY,
                // The legacy soft delete. "No" is the one thing in this register that reaches past
                // it, and it shows the deleted orders alone, as the legacy grid does.
                options: yesNo(),
            },
            {
                key: K.grid,
                kind: 'choice',
                label: 'Grid / Report Type',
                chipLabel: 'View',
                // A display control rather than a filter: Summary drops the product-thumbnail
                // column so more rows fit on a screen. Neutral is Detail, so it draws no chip
                // until somebody switches it.
                neutral: GRID_DETAIL,
                options: [
                    { value: GRID_DETAIL, label: 'Detail' },
                    { value: GRID_SUMMARY, label: 'Summary' },
                ],
            },
        ]),
    };

    return [orderGroup, productGroup, logisticsGroup];
}

/** A choice whose value is an id, or undefined when it is neutral or absent. */
function id(applied: AppliedFilters<OrderFilterKey>, key: OrderFilterKey): number | undefined {
    const value = filterText(applied, key);
    return value === undefined || value === ANY || !/^\d+$/.test(value) ? undefined : Number(value);
}

/** A Yes/No choice as a boolean, or undefined when it is neutral. */
function flag(applied: AppliedFilters<OrderFilterKey>, key: OrderFilterKey): boolean | undefined {
    const value = filterText(applied, key);
    return value === YES ? true : value === NO ? false : undefined;
}

/** A text or date choice, or undefined when it is blank. */
function text(applied: AppliedFilters<OrderFilterKey>, key: OrderFilterKey): string | undefined {
    const value = filterText(applied, key)?.trim();
    return value ? value : undefined;
}

function choice(applied: AppliedFilters<OrderFilterKey>, key: OrderFilterKey): string | undefined {
    const value = text(applied, key);
    return value === ANY ? undefined : value;
}

/** Whether the table drops its thumbnail column, which is the drawer's own display control. */
export function isSummaryView(applied: AppliedFilters<OrderFilterKey>): boolean {
    return filterText(applied, ORDER_FILTER_KEYS.grid) === GRID_SUMMARY;
}

/**
 * The applied set as the API's query string. Every neutral value drops out here rather than
 * travelling as "All", so the server never has to know what this screen calls "not applied".
 */
export function orderListQuery(
    applied: AppliedFilters<OrderFilterKey>,
    q: string,
    sort: { key: string; direction: 'asc' | 'desc' } | null,
    page: number,
    pageSize: number,
): OrderListQuery {
    const K = ORDER_FILTER_KEYS;
    return {
        q: q.trim() || undefined,
        orderNo: text(applied, K.orderNo),
        invoiceNo: text(applied, K.invoiceNo),
        reference: text(applied, K.reference),
        tracking: text(applied, K.tracking),
        customerId: id(applied, K.customer),
        rmId: id(applied, K.rm),
        assignedToId: id(applied, K.assignedTo),
        invoiceTypeId: id(applied, K.invoiceType),
        saleSourceId: id(applied, K.saleSource),
        status: choice(applied, K.status),
        payment: choice(applied, K.payment),
        dateField: choice(applied, K.dateField),
        // One control, two query parameters: the API takes the ends separately, as the legacy
        // procedure's @FromDate and @ToDate do.
        from: rangeEnds(filterText(applied, K.dates)).from,
        to: rangeEnds(filterText(applied, K.dates)).to,
        productId: id(applied, K.product),
        lineStatusId: id(applied, K.itemStatus),
        brandId: id(applied, K.brand),
        taxId: id(applied, K.productTax),
        supplierId: id(applied, K.supplier),
        commission: flag(applied, K.commission),
        commissionModel: choice(applied, K.commissionModel),
        commissionStatus: choice(applied, K.commissionStatus),
        sizeTypeId: id(applied, K.sizeType),
        sizeId: id(applied, K.size),
        categoryGroupId: id(applied, K.categoryGroup),
        categoryId: id(applied, K.cat1),
        categorySubId: id(applied, K.cat2),
        categorySubChildId: id(applied, K.cat3),
        preOrder: flag(applied, K.preOrder),
        dropShip: flag(applied, K.dropShip),
        active: flag(applied, K.active),
        sort: sort?.key,
        dir: sort?.direction,
        page,
        pageSize,
    } as OrderListQuery;
}

/**
 * The keys that reach the API. `grid` is not one: it changes which column the table draws and
 * narrows nothing, so it rides the URL but never the query. Exported so the route can carry every
 * key without listing them twice.
 */
export const ORDER_FILTER_KEY_LIST: readonly OrderFilterKey[] = Object.values(ORDER_FILTER_KEYS);

export function isOrderFilterKey(value: string): value is OrderFilterKey {
    return (ORDER_FILTER_KEY_LIST as readonly string[]).includes(value);
}

/** Narrowing helper for the drawer's choice fields, which the route uses to validate a pasted URL. */
export type OrderChoiceField = ChoiceFilterField<OrderFilterKey>;
